import { FabButton, ISaveTablePlanRequest } from '@localina/core';
import { AddIcon } from '@localina/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAreas } from '../../api/queries/restaurantAreas';
import { useRestaurant } from '../../api/queries/restaurants';
import { useCreateTablePlan } from '../../api/queries/restaurantTablePlans';
import { Page } from '../../components';
import { TablePlanCreationView } from '../../components/TablePlanCreationView';
import { Path } from '../../enums';
import { PathUtils } from '../../utils';
import TablePlanCard from './TablePlanCard';

const TablePlans: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [openTablePlanDrawer, setOpenTablePlanDrawer] = React.useState(false);

    const [creationAreaId, setCreationAreaId] = React.useState<string | undefined>(undefined);

    const restaurantQuery = useRestaurant();
    const createTablePlanMutation = useCreateTablePlan();
    const areasQuery = useAreas();

    const openTablePlanDetails = (areaId?: string) => {
        setCreationAreaId(areaId);
        setOpenTablePlanDrawer(true);
    };

    const closeTablePlanDetails = () => {
        setOpenTablePlanDrawer(false);
        setCreationAreaId(undefined);
    };

    const handleTablePlanCreationSave = (tablePlan?: ISaveTablePlanRequest) => {
        if (restaurantQuery.data && tablePlan) {
            createTablePlanMutation.mutate(
                { tablePlan },
                {
                    onSuccess: (tablePlanId) => {
                        closeTablePlanDetails();
                        navigate(
                            PathUtils.generateUrl(Path.RESTAURANT_TABLE_PLAN_DETAILS, {
                                restaurantId: restaurantQuery.data?.id,
                                tablePlanId,
                            }),
                        );
                    },
                },
            );
        }
    };

    const isLoading = createTablePlanMutation.isLoading || restaurantQuery.isInitialLoading;

    const allAreas = areasQuery.data?.areas ?? [];

    const areas = [
        ...allAreas.filter((area) => area.tablePlans?.length),
        ...allAreas.filter((area) => !area.tablePlans?.length),
    ];
    const planCount = areas.reduce((total, area) => total + (area.tablePlans?.length ?? 0), 0);
    const subtitle = areasQuery.isSuccess
        ? t('table.plans.subtitle', {
              plans: t('table.plans.plansCount', { count: planCount }),
              areas: t('table.plans.areasCount', { count: areas.length }),
          })
        : undefined;

    return (
        <Page
            name="table-plans"
            isLoading={isLoading}
            title={{ value: t('common.menu.categories.settings.menu.table.plan'), subtitle }}
        >
            {}
            <div className="table-plan-cards">
                {areas.map((area) =>
                    area.tablePlans?.length ? (
                        area.tablePlans.map((tablePlan) => (
                            <TablePlanCard
                                key={tablePlan.id}
                                restaurant={restaurantQuery.data}
                                area={area}
                                tablePlan={tablePlan}
                            />
                        ))
                    ) : (
                        <div
                            key={area.id}
                            className="table-plan-card is-empty"
                            role="button"
                            tabIndex={0}
                            onClick={() => openTablePlanDetails(area.id)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    openTablePlanDetails(area.id);
                                }
                            }}
                        >
                            <span className="table-plan-card__empty__icon">
                                <AddIcon />
                            </span>
                            <span className="table-plan-card__name">{area.name}</span>
                            <span className="table-plan-card__meta">{t('table.plans.empty')}</span>
                            <span className="table-plan-card__empty__cta">{t('table.plans.createPlan')}</span>
                        </div>
                    ),
                )}
            </div>
            <FabButton
                icon={<AddIcon />}
                onClick={() => openTablePlanDetails()}
                className="table-plans-add-button"
                fixed
            />
            <TablePlanCreationView
                areaId={creationAreaId}
                hasError={createTablePlanMutation.isError}
                openDialog={openTablePlanDrawer}
                onSave={handleTablePlanCreationSave}
                onClose={closeTablePlanDetails}
            />
        </Page>
    );
};

export default TablePlans;
